import { useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { matchPath, useHistory } from 'react-router-dom';

import { fullPageRedirect } from '@hungryroot/environment';
import {
  CYO_ROUTE,
  ROUTE_MINI_QUIZ_MEAL_FREQUENCIES,
  ROUTE_QUIZ_EATING_HABITS_DIETARY_NEEDS,
  ROUTE_QUIZ_GOALS,
  ROUTE_QUIZ_MACRO_NUTRIENT,
  ROUTE_QUIZ_READY_MADE,
  ROUTE_QUIZ_POLARIZING,
  ROUTE_QUIZ_FOOD_QUALITIES,
} from '@hungryroot/routes';
import { Button, Heading, Paragraph } from '@hungryroot/ui-kit';
import { QuizContext } from '../../quiz-context';
import { type Quiz } from '../../quiz-hooks';
import {
  getMiniQuizAnswerState,
  isActiveMiniQuizTailRoute,
  isMiniQuizOptionalQuestionRoute,
} from '../../utils/miniQuizQuestion';
import { Footer as QuizFooter } from '../Footer/Footer';
import { QuizForm } from '../QuizForm/QuizForm';
import { QuizToastContainer } from '../QuizToastContainer/QuizToastContainer';
import { QuizQuestionScreenSubmitContext } from './quiz-question-screen-submit';
import styles from './QuizQuestionScreen.module.css';
import layoutStyles from '../../Page/layout.module.css';

export interface Props {
  heroText?: ReactNode;
  subCopy?: ReactNode;
  children?: ReactNode;
  sectionLabel?: ReactNode;
  testid?: string;
  className?: string;
  secondaryCyid?: string;
  submitButtonDisabled?: boolean;
  onSubmitAnalytics?: () => void;
  onSubmit?: (quiz: Quiz) => void;
  onBack?: () => void;
}

const BACK_BUTTON_PROPS = {
  style: 'default',
  size: 'medium',
  prominence: 'secondary',
} as const;

const SUBMIT_BUTTON_PROPS = {
  style: 'default',
  size: 'medium',
  prominence: 'primary',
} as const;

const CONTINUE_BUTTON_PROPS = {
  ...SUBMIT_BUTTON_PROPS,
  width: 'full-width',
} as const;

function getShouldSkip(loc: string, quiz: Quiz) {
  switch (loc) {
    case ROUTE_QUIZ_EATING_HABITS_DIETARY_NEEDS:
      return quiz.dietary === null || !quiz.dietary.length;
    case ROUTE_QUIZ_MACRO_NUTRIENT:
      return (
        quiz.macroNutrient === undefined ||
        quiz.macroNutrient === null ||
        !quiz.macroNutrient.length
      );
    case ROUTE_QUIZ_POLARIZING:
      return quiz.dislike === null || !quiz.dislike.length;
    case ROUTE_QUIZ_READY_MADE:
      return (
        quiz.readyMade === undefined ||
        quiz.readyMade === null ||
        !quiz.readyMade.length
      );
    case ROUTE_QUIZ_GOALS:
      return false;
    case ROUTE_QUIZ_FOOD_QUALITIES:
      return (
        quiz.foodQualities === undefined ||
        quiz.foodQualities === null ||
        !quiz.foodQualities.length
      );
    default:
      return false;
  }
}

/**
 * @deprecated
 *
 * We need to talk about an alternative to this component because it's too abstracted and too coupled with old expectations.
 */
export function QuizQuestionScreen(props: Props) {
  const {
    testid,
    secondaryCyid,
    className,
    children,
    subCopy,
    heroText,
    submitButtonDisabled,
    onSubmitAnalytics,
    onSubmit,
    onBack,
  } = props;
  const history = useHistory();
  const {
    location: { pathname },
  } = history;

  const context = useContext(QuizContext);
  const {
    navigationState,
    quizToast,
    quiz,
    onSubmit: quizContextOnSubmit,
    isMiniQuiz,
  } = context;

  const onSubmitFn = (nextQuiz: Quiz) =>
    typeof onSubmit === 'function'
      ? onSubmit(nextQuiz)
      : quizContextOnSubmit(nextQuiz);

  function requestSubmit(nextQuiz: Quiz) {
    onSubmitAnalytics?.();
    onSubmitFn(nextQuiz);
  }

  const isSkip = getShouldSkip(pathname, quiz);
  const isTail = isActiveMiniQuizTailRoute(pathname);
  const useMiniQuizChrome = isMiniQuiz && !isTail;
  const { hasValue } = getMiniQuizAnswerState({
    pathname,
    quiz,
  });
  const [hadAnswerOnMount] = useState(hasValue);
  const isOptional = isMiniQuizOptionalQuestionRoute(pathname);
  const showMiniQuizContinue = useMiniQuizChrome && (isOptional || hasValue);
  const hasShownContinue = useRef(false);
  const delayCurrentContinue = useRef(false);

  if (showMiniQuizContinue && !hasShownContinue.current) {
    delayCurrentContinue.current = isOptional || hadAnswerOnMount;
    hasShownContinue.current = true;
  }
  if (!showMiniQuizContinue) {
    delayCurrentContinue.current = false;
  }

  const { prev } = navigationState;
  const { clearToasts } = quizToast || {};
  const clearToastsRef = useRef(clearToasts);
  clearToastsRef.current = clearToasts;

  useEffect(() => {
    return () => {
      clearToastsRef.current?.();
    };
  }, []);

  const backButton =
    !useMiniQuizChrome && prev !== null ? (
      <Button
        {...BACK_BUTTON_PROPS}
        onClick={(e) => {
          e.preventDefault();
          onBack?.();
          if (
            isTail &&
            matchPath(pathname, {
              path: ROUTE_MINI_QUIZ_MEAL_FREQUENCIES,
              exact: true,
              strict: false,
            })
          ) {
            fullPageRedirect(CYO_ROUTE);
            return;
          }
          history.push(prev, {
            from: pathname,
          });
        }}
      >
        Back
      </Button>
    ) : undefined;

  const submitButton = useMiniQuizChrome ? (
    showMiniQuizContinue ? (
      <Button
        {...CONTINUE_BUTTON_PROPS}
        className={clsx(
          layoutStyles['slide-in'],
          delayCurrentContinue.current && styles['continue-after-answers']
        )}
        buttonType="submit"
        testid="quiz-button-next"
        onClick={(e) => {
          if (submitButtonDisabled === true) {
            e.preventDefault();
          }

          clearToasts?.();
        }}
        disabled={submitButtonDisabled}
      >
        Continue
      </Button>
    ) : undefined
  ) : (
    <Button
      {...SUBMIT_BUTTON_PROPS}
      buttonType="submit"
      testid="quiz-button-next"
      onClick={(e) => {
        if (submitButtonDisabled === true) {
          e.preventDefault();
        }

        clearToasts?.();
      }}
      disabled={submitButtonDisabled}
    >
      {isSkip ? 'Skip' : 'Next'}
    </Button>
  );

  const footer = (
    <QuizFooter
      floating={useMiniQuizChrome}
      childrenBefore={<QuizToastContainer testid="quiz-toast-container" />}
      backButton={backButton}
      submitButton={submitButton}
    />
  );

  return (
    <QuizQuestionScreenSubmitContext.Provider value={requestSubmit}>
      <QuizForm
        footer={footer}
        onSubmit={() => {
          requestSubmit(quiz);
        }}
        childrenContainerClassName={styles['delay-answers']}
        secondaryCyid={secondaryCyid}
        className={className}
        alignStart={useMiniQuizChrome}
        hero={
          heroText && (
            <Heading testid={`${testid}-question-section-hero`} variant="H1Alt">
              {heroText}
            </Heading>
          )
        }
        subCopy={
          subCopy ? (
            typeof subCopy === 'string' ? (
              <Paragraph
                testid={`${testid}-question-section-subcopy`}
                className={styles['question-section-subcopy']}
              >
                {subCopy}
              </Paragraph>
            ) : (
              subCopy
            )
          ) : null
        }
      >
        <section
          data-testid={testid}
          className={clsx(
            styles.center,
            useMiniQuizChrome && styles['align-start']
          )}
        >
          {children}
        </section>
      </QuizForm>
    </QuizQuestionScreenSubmitContext.Provider>
  );
}
